import { create } from 'zustand';
import { AppState, UserProfile, DailyRecord, Notification, TimerState, ActivityType, UserStats } from '../types';
import { dailyRecordService } from '../lib/database';
import { userService } from '../lib/database';

const initialTimerState: TimerState = {
  isRunning: false,
  mode: null,
  elapsedTime: 0,
  startTime: null,
  targetTime: 0
};

export const useAppStore = create<AppState>((set, get) => ({
  // 상태
  user: null,
  dailyRecord: null,
  currentDay: 0,
  theme: 'light',
  soundEnabled: true,
  isOnboarded: false,
  notifications: [],
  showCelebration: false,
  timer: initialTimerState,
  activeTab: 'dashboard',

  // 액션
  setUser: (user: UserProfile | null) => {
    try {
      if (!user) {
        set({ isOnboarded: false });
        return;
      }

      set({
        user,
        isOnboarded: true
      });

      get().loadTodayRecord();
    } catch (error) {
      console.error('사용자 설정 중 오류:', error);
      get().addNotification({
        type: 'error',
        message: '사용자 정보를 불러오는 중 문제가 발생했습니다.',
        read: false
      });
    }
  },

  updateDailyProgress: async (record: Partial<DailyRecord>) => {
    try {
      const state = get();
      if (!state.user || !state.dailyRecord) return;

      const updatedRecord = { ...state.dailyRecord, ...record };
      if (updatedRecord.id) {
        await dailyRecordService.updateDailyRecord(String(updatedRecord.id), updatedRecord);
      }

      set({ dailyRecord: updatedRecord });

      get().showCelebrationMessage();
      get().addNotification({
        type: 'success',
        message: '오늘의 목표를 모두 달성했습니다!',
        read: false
      });
    } catch (error) {
      console.error('진행상황 업데이트 중 오류:', error);
      get().addNotification({
        type: 'error',
        message: '진행상황을 저장하는 중 문제가 발생했습니다.',
        read: false
      });
    }
  },

  startTimer: () => {
    const now = Date.now();
    set({
      timer: {
        isRunning: true,
        mode: 'physical',
        elapsedTime: 0,
        startTime: now,
        targetTime: 0
      }
    });
  },

  stopTimer: () => {
    set({ timer: initialTimerState });
  },

  pauseTimer: () => {
    set(state => ({
      timer: {
        ...state.timer,
        isRunning: false
      }
    }));
  },

  completeActivity: (activity: ActivityType) => {
    try {
      const state = get();
      if (!state.dailyRecord) return;

      const updates: Partial<DailyRecord> = {
        completedActivities: [
          ...(state.dailyRecord.completedActivities || []),
          String(activity.id)
        ],
        notes: state.dailyRecord.notes || ''
      };

      get().updateDailyProgress(updates);
      get().stopTimer();

      get().addNotification({
        type: 'success',
        message: `${activity.name} 활동을 완료했습니다!`,
        read: false
      });
    } catch (error) {
      console.error('활동 완료 처리 중 오류:', error);
      get().addNotification({
        type: 'error',
        message: '활동 완료를 저장하는 중 문제가 발생했습니다.',
        read: false
      });
    }
  },

  updateUserStats: async (stats: Partial<UserStats>) => {
    try {
      const state = get();
      if (!state.user) return;

      const updatedStats: UserStats = {
        ...state.user.stats,
        ...stats,
        lastActiveDate: new Date()
      };

      const updatedUser = await userService.updateUser(state.user.id, {
        stats: updatedStats
      });

      set({ user: updatedUser });
    } catch (error) {
      console.error('통계 업데이트 중 오류:', error);
      get().addNotification({
        type: 'error',
        message: '통계 업데이트 중 문제가 발생했습니다.',
        read: false
      });
    }
  },

  loadTodayRecord: async () => {
    try {
      const state = get();
      if (!state.user) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const record = await dailyRecordService.getDailyRecord(state.user.id, today);
      if (record) {
        set({ dailyRecord: record });
      }
    } catch (error) {
      console.error('오늘의 기록 로드 중 오류:', error);
      get().addNotification({
        type: 'error',
        message: '오늘의 기록을 불러오는 중 문제가 발생했습니다.',
        read: false
      });
    }
  },

  initializeUser: async () => {
    try {
      const state = get();
      if (!state.user) return;

      await get().loadTodayRecord();
      await get().updateUserStats({});
    } catch (error) {
      console.error('사용자 초기화 중 오류:', error);
    }
  },

  toggleTheme: () => {
    set(state => ({
      theme: state.theme === 'light' ? 'dark' : 'light'
    }));
  },

  toggleSound: () => {
    set((state) => ({
      ...state,
      soundEnabled: !state.soundEnabled
    }));
  },

  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      createdAt: new Date()
    };

    set((state) => ({
      ...state,
      notifications: [...state.notifications, newNotification]
    }));
  },

  removeNotification: (id: string) => {
    set(state => ({
      notifications: state.notifications.filter(n => n.id !== id)
    }));
  },

  setActiveTab: (tab: string) => {
    set({ activeTab: tab });
  },

  showCelebrationMessage: () => {
    set({ showCelebration: true });
  },

  hideCelebrationMessage: () => {
    set({ showCelebration: false });
  }
}));

// 안전한 스토어 초기화
setTimeout(() => {
  try {
    useAppStore.getState().initializeUser();
  } catch (error) {
    console.error('스토어 초기화 중 오류:', error);
  }
}, 100); 